import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Text } from '@dropbox/dig-components/typography';
import { Box } from '@dropbox/dig-foundations';
import { UIIcon } from '@dropbox/dig-icons';
import {
  ArchiveLine,
  EmailLine,
  FileLine,
  PersonMultipleLine,
  SendLine,
} from '@dropbox/dig-icons/assets';

export type FaxPlanHighlightsPlan = {
  num_fax_pages?: number | null;
  num_seats_max?: number | null;
  name?: string | null;
  is_multi_fax_enabled?: boolean | null;
};

const Highlight = ({
  icon,
  children,
  rowClassName,
}: React.PropsWithChildren<{
  icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
  rowClassName?: string;
}>) => {
  const content = (
    <>
      <UIIcon src={icon} />
      <Text variant="paragraph">{children}</Text>
    </>
  );

  if (rowClassName) {
    return <div className={rowClassName}>{content}</div>;
  }

  return (
    <Box display="flex" alignItems="flex-start" style={{ gap: '8px' }}>
      {content}
    </Box>
  );
};

export const FaxPlanHighlights: React.FunctionComponent<{
  plan: FaxPlanHighlightsPlan;
  className?: string;
  rowClassName?: string;
}> = ({ plan, className, rowClassName }) => {
  const rows = (
    <>
      {plan.num_fax_pages != null && (
        <Highlight icon={FileLine} rowClassName={rowClassName}>
          <FormattedMessage
            id=""
            description="Text indicating how many fax pages per month the given fax plan has."
            defaultMessage="{numFaxPages, number}+ fax pages per month"
            values={{
              numFaxPages: plan.num_fax_pages,
            }}
          />
        </Highlight>
      )}
      {plan.num_seats_max != null && (
        <Highlight icon={PersonMultipleLine} rowClassName={rowClassName}>
          <FormattedMessage
            id=""
            description="Text indicating how many senders the given fax plan has."
            defaultMessage="{numSenders, plural, one {# sender} other {# senders}}"
            values={{
              numSenders: plan.num_seats_max,
            }}
          />
        </Highlight>
      )}
      <Highlight icon={SendLine} rowClassName={rowClassName}>
        <FormattedMessage
          id=""
          description="Paid fax plan feature name; shown next to an icon."
          defaultMessage="Send and receive faxes"
        />
      </Highlight>
      <Highlight icon={EmailLine} rowClassName={rowClassName}>
        <FormattedMessage
          id=""
          description="Paid fax plan feature name; shown next to an icon."
          defaultMessage="Email to fax"
        />
      </Highlight>
      {plan.is_multi_fax_enabled && (
        <Highlight icon={ArchiveLine} rowClassName={rowClassName}>
          <FormattedMessage
            id=""
            description="Paid fax plan feature name; shown next to an icon."
            defaultMessage="Multiple fax recipients"
          />
        </Highlight>
      )}
      {plan.name !== 'Home Office' && (
        <Highlight icon={FileLine} rowClassName={rowClassName}>
          <FormattedMessage
            id=""
            description="Paid fax plan feature name; shown next to an icon."
            defaultMessage="Fax number porting"
          />
        </Highlight>
      )}
    </>
  );

  if (className) {
    return (
      <div className={className} data-testid="fax-highlights">
        {rows}
      </div>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{ gap: '12px' }}
      data-testid="fax-highlights"
    >
      {rows}
    </Box>
  );
};
